import React, { useEffect, useRef, useState } from "react";
import type { GameState } from "../../shared/types";

import Overlay from "./Overlay";
import { RESPAWN_DELAY, endsAt } from "core/src/core/respawn.js";
import s from "./RespawnCountdown.module.scss";

// A Respawn round (core respawn.js): your worm crashed, and you are back in a
// moment. Read from the game events (a client's copy of an online round has
// no idea when the host has you due): your last crash, and no spawn since.
interface Props {
  gameState: GameState;
  youIds: string[];
  getRenderTime: () => number;
}

function secondsLeft(gs: any, youIds: string[], now: number): number {
  if (!gs || !gs.respawn || !youIds.length) return 0;
  const ev = gs.gameEvents || [];
  let best = 0;
  for (const id of youIds) {
    let died = -1;
    for (let i = ev.length - 1; i >= 0; i--) {
      const e = ev[i];
      if (e.playerId !== id) continue;
      if (e.type === "worm_spawned") break;
      if (e.type === "player_died") { died = e.time; break; }
    }
    if (died < 0) continue;
    const back = died + RESPAWN_DELAY;
    if (back >= endsAt(gs)) continue; // the round is over first
    best = Math.max(best, back - now);
  }
  return best;
}

export default function RespawnCountdown({ gameState, youIds, getRenderTime }: Props) {
  const [, setTick] = useState(0);
  const propsRef = useRef({ gameState, youIds, getRenderTime });
  propsRef.current = { gameState, youIds, getRenderTime };
  // a light re-render while the number would change (never while you are in)
  useEffect(() => {
    let last = 0;
    const id = window.setInterval(() => {
      const p = propsRef.current;
      const n = Math.ceil(secondsLeft(p.gameState, p.youIds, p.getRenderTime()));
      if (n !== last) { last = n; setTick((t) => t + 1); }
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  const left = Math.ceil(secondsLeft(gameState, youIds, getRenderTime()));
  if (left <= 0) return null;
  return (
    <Overlay gameState={gameState} className={s.wrap}>
      <div className={s.card}>Back in <b>{left}</b></div>
    </Overlay>
  );
}
